/**
 * DailyOperationsCard Component
 * Displays daily checkpoint status: Stock-In → Wastage → TSR
 * Shows 24-hour countdown, checkpoint completion status, and navigation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faClock, 
  faExclamationTriangle, 
  faLock,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { Calendar } from 'lucide-react';
import { onDataChanged, onScreenResumed } from '../utils/dataChangeSignal';
import { onIstHours } from '../utils/dayBoundary';
import MonthlyComplianceCalendar from './MonthlyComplianceCalendar';
import './DailyOperationsCard.css';

const DailyOperationsCard = ({ onNavigate, businessId }) => {
  const [operationStatus, setOperationStatus] = useState(null);
  const [error, setError] = useState(null);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const fetchOperationStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/operations/today-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-business-id': businessId
        }
      });

      const result = await response.json();

      if (result.success) {
        setOperationStatus(result.data);
        setError(null);
        // Show overdue modal if overdue and TSR not submitted
        if (result.data.isOverdue && !result.data.tsrSubmitted && !sessionStorage.getItem('overdueModalShown')) {
          setShowOverdueModal(true);
          sessionStorage.setItem('overdueModalShown', 'true');
        }
      } else {
        console.error('❌ API returned error:', result.error);
        setError(result.error || 'Failed to load operations status');
      }
    } catch (err) {
      console.error('❌ Error fetching operation status:', err);
      setError(`Failed to load daily operations: ${err.message}`);
    }
  }, [businessId, API_URL]);

  // Refreshed on what moves a checkpoint, not on a timer.
  //
  // The three checkpoints — stock-in, wastage, sales report — are each completed
  // by a write, and a write announces itself (here and in other tabs). Whether
  // the day is overdue or locked turns over on the clock instead, at IST midnight
  // and at 02:00 IST, so those two instants are waited for rather than polled at.
  //
  // Between them nothing is asked. The "hours remaining" figure therefore stops
  // counting down on a card left untouched for hours; it is brought up to date
  // the moment the tab is looked at again, which is the only time anyone reads it.
  useEffect(() => {
    fetchOperationStatus();

    const refresh = () => { fetchOperationStatus(); };
    const stopListening = onDataChanged(refresh);
    const stopResuming = onScreenResumed(refresh);
    const stopSchedule = onIstHours([0, 2], refresh);

    return () => {
      stopListening();
      stopResuming();
      stopSchedule();
    };
  }, [fetchOperationStatus]);

  const getCheckpointIcon = (completed, isLocked, isOverdue) => {
    if (completed) {
      return <FontAwesomeIcon icon={faCheckCircle} className="checkpoint-icon completed" />;
    } else if (isLocked) {
      return <FontAwesomeIcon icon={faLock} className="checkpoint-icon locked" />;
    } else if (isOverdue) {
      return <FontAwesomeIcon icon={faExclamationTriangle} className="checkpoint-icon overdue" />;
    } else {
      return <FontAwesomeIcon icon={faClock} className="checkpoint-icon pending" />;
    }
  };

  const getCheckpointClass = (completed, isLocked, isOverdue) => {
    if (completed) return 'checkpoint-status completed';
    if (isLocked) return 'checkpoint-status locked';
    if (isOverdue) return 'checkpoint-status overdue';
    return 'checkpoint-status pending';
  };

  const handleCheckpointClick = (checkpoint) => {
    if (!operationStatus) return;

    // Check prerequisites before navigation
    if (checkpoint === 'stockin') {
      onNavigate('stock-in-scan');
    } else if (checkpoint === 'wastage') {
      onNavigate('inventory-stock-out');
    } else if (checkpoint === 'tsr') {
      if (!operationStatus.wastageRecorded) {
        alert('Please record wastage (or mark as none) before submitting TSR.');
        return;
      }
      onNavigate('todays-sales-report');
    }
  };

  const handleOverdueModalClose = () => {
    setShowOverdueModal(false);
  };

  if (error) {
    return (
      <div className="daily-operations-card error">
        <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
        <h4>Unable to load daily operations</h4>
        <p>{error}</p>
        <button onClick={fetchOperationStatus} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (!operationStatus) {
    return (
      <div className="daily-operations-card loading">
        <p className="daily-operations-loading-text">Loading daily operations...</p>
      </div>
    );
  }

  // The day is closed out. Same rule the compliance calendar paints green on:
  // wastage recorded AND the sales report submitted. TSR already requires
  // wastage behind it, so this reads as a restatement — but stating it here
  // keeps the card and the calendar from ever disagreeing about a day if that
  // prerequisite is relaxed or bypassed by a recovery path.
  if (operationStatus.wastageRecorded && operationStatus.tsrSubmitted) {
    return (
      <div className="daily-operations-card all-complete">
        <div className="operations-header" style={{ width: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: showCalendar ? '12px' : '0', paddingBottom: showCalendar ? '12px' : '0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon={faCheckCircle} className="complete-icon" />
            <h3 style={{ fontSize: '18px', margin: 0 }}>Daily Operations Complete!</h3>
          </div>
          <button 
            type="button" 
            className={`calendar-toggle-btn ${showCalendar ? 'active' : ''}`}
            onClick={() => setShowCalendar(prev => !prev)}
            title={showCalendar ? "Hide Calendar" : "View Compliance Calendar"}
            style={{ position: 'absolute', right: 0 }}
          >
            <Calendar size={16} />
          </button>
        </div>

        {showCalendar && (
          <MonthlyComplianceCalendar 
            businessId={businessId}
            onNavigate={onNavigate}
            currentOperationStatus={operationStatus}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className={`daily-operations-card ${operationStatus.isOverdue ? 'overdue-border' : ''}`}>
        <div className="operations-header">
          <h3 style={{ fontSize: '18px', margin: 0 }}>Daily Operations</h3>
          <div className="header-right-actions">
            <button 
              type="button" 
              className={`calendar-toggle-btn ${showCalendar ? 'active' : ''}`}
              onClick={() => setShowCalendar(prev => !prev)}
              title={showCalendar ? "Show Checkpoints" : "View Compliance Calendar"}
            >
              <Calendar size={16} />
            </button>
            <div className="time-remaining">
              {operationStatus.isOverdue ? (
                <span className="overdue-badge">
                  <FontAwesomeIcon icon={faExclamationTriangle} /> OVERDUE
                </span>
              ) : (
                <span className="time-badge">
                  <FontAwesomeIcon icon={faClock} /> {operationStatus.hoursRemaining}h left
                </span>
              )}
            </div>
          </div>
        </div>

        {showCalendar ? (
          <MonthlyComplianceCalendar 
            businessId={businessId}
            onNavigate={onNavigate}
            currentOperationStatus={operationStatus}
          />
        ) : (
          <>
            <div className="checkpoints-container">
              {/* Checkpoint 1: Stock-In */}
              <div
                className={getCheckpointClass(
                  operationStatus.stockinCompleted,
                  false,
                  operationStatus.isOverdue && !operationStatus.stockinCompleted
                )}
                onClick={() => handleCheckpointClick('stockin')}
                style={{ cursor: 'pointer' }}
              >
                <div className="checkpoint-icon-wrapper">
                  {getCheckpointIcon(
                    operationStatus.stockinCompleted,
                    false,
                    operationStatus.isOverdue && !operationStatus.stockinCompleted
                  )}
                </div>
                <div className="checkpoint-details">
                  <h4>Stock-In</h4>
                  <p>
                    {operationStatus.stockinCompleted
                      ? `Completed at ${new Date(operationStatus.stockinCompletedAt).toLocaleTimeString()}`
                      : 'Click to add inventory batches'}
                  </p>
                </div>
              </div>

              {/* Checkpoint 2: Wastage */}
              <div
                className={getCheckpointClass(
                  operationStatus.wastageRecorded,
                  !operationStatus.stockinCompleted,
                  operationStatus.isOverdue && !operationStatus.wastageRecorded
                )}
                onClick={() => operationStatus.stockinCompleted && handleCheckpointClick('wastage')}
                style={{ cursor: operationStatus.stockinCompleted ? 'pointer' : 'not-allowed' }}
              >
                <div className="checkpoint-icon-wrapper">
                  {getCheckpointIcon(
                    operationStatus.wastageRecorded,
                    !operationStatus.stockinCompleted,
                    operationStatus.isOverdue && !operationStatus.wastageRecorded && operationStatus.stockinCompleted
                  )}
                </div>
                <div className="checkpoint-details">
                  <h4>Wastage</h4>
                  <p>
                    {operationStatus.wastageRecorded
                      ? `Recorded at ${new Date(operationStatus.wastageRecordedAt).toLocaleTimeString()}`
                      : operationStatus.stockinCompleted
                      ? 'Click to record wastage'
                      : 'Complete Stock-In first'}
                  </p>
                </div>
              </div>

              {/* Checkpoint 3: TSR */}
              <div
                className={getCheckpointClass(
                  operationStatus.tsrSubmitted,
                  !operationStatus.wastageRecorded,
                  operationStatus.isOverdue && !operationStatus.tsrSubmitted
                )}
                onClick={() => operationStatus.wastageRecorded && handleCheckpointClick('tsr')}
                style={{ cursor: operationStatus.wastageRecorded ? 'pointer' : 'not-allowed' }}
              >
                <div className="checkpoint-icon-wrapper">
                  {getCheckpointIcon(
                    operationStatus.tsrSubmitted,
                    !operationStatus.wastageRecorded,
                    operationStatus.isOverdue && !operationStatus.tsrSubmitted && operationStatus.wastageRecorded
                  )}
                </div>
                <div className="checkpoint-details">
                  <h4>Today's Sales Report</h4>
                  <p>
                    {operationStatus.tsrSubmitted
                      ? `Submitted at ${new Date(operationStatus.tsrSubmittedAt).toLocaleTimeString()}`
                      : operationStatus.wastageRecorded
                      ? 'Click to submit sales report'
                      : 'Complete previous steps first'}
                  </p>
                </div>
              </div>
            </div>

            <div className="operations-footer">
              <p className="workflow-reminder">
                <FontAwesomeIcon icon={faCheckCircle} className="workflow-check-icon" />
                <span className="workflow-reminder-text">
                  <span>Complete in sequence: Stock-In</span>
                  <span className="workflow-arrow-separator">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                  <span>Wastage</span>
                  <span className="workflow-arrow-separator">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                  <span>TSR</span>
                </span>
              </p>
            </div>
          </>
        )}
      </div>

      {/* Overdue Modal */}
      {showOverdueModal && (
        <div className="overdue-modal-overlay" onClick={handleOverdueModalClose}>
          <div className="overdue-modal-content" onClick={(e) => e.stopPropagation()}>
            <FontAwesomeIcon icon={faExclamationTriangle} className="overdue-modal-icon" />
            <h2>Daily Operations Overdue!</h2>
            <p>
              Your daily operations have exceeded the 24-hour deadline. Please complete the remaining checkpoints immediately.
            </p>
            <div className="overdue-modal-details">
              <p><strong>Time elapsed:</strong> {operationStatus.hoursSinceStart}h</p>
              <p><strong>Started:</strong> {new Date(operationStatus.dayStartedAt).toLocaleString()}</p>
            </div>
            <button className="overdue-modal-btn" onClick={handleOverdueModalClose}>
              I Understand
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DailyOperationsCard;
